import React, {useCallback, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {v4 as uuid} from 'uuid';
import {EIconSmallButton} from 'webapp-lib/Components';
import {useMountEffect} from 'webapp-lib/utils';

import {SelectBox, SelectBoxStartingPoint} from '#/form';
import {ChevronLargeLeft, ChevronLargeRight} from '_/assets/images';
import {I18NextNS} from '_/i18n';
import {typedObserver} from '_/utilities/typed-observer';
import {Positioning} from '_/utilities/types';

import TabItem, {Item, Props as TabBarItemProps} from './Item';
import {
  ChevronDown,
  FadeElementsContainer,
  LeftFade,
  RightFade,
  ScrollWidthExtenderLeft,
  ScrollWidthExtenderRight,
  StyledChevron,
  StyledTabBar,
  TabsContainer,
} from './styles';

export interface Props<T extends Item> {
  className?: string;
  items: T[];
  activeItem?: T;
  onActiveItemChange: (arg0: T) => void;
  onClose?: (arg0: T) => void;
  onCloseAll?: () => void;
  ItemComponent?: React.FunctionComponent<TabBarItemProps<T>>;
}

export function TabBarComponent<T extends Item>({
  className,
  items,
  activeItem,
  onActiveItemChange,
  onClose,
  onCloseAll,
  ItemComponent = TabItem,
}: Props<T>): React.ReactElement {
  const {t} = useTranslation<I18NextNS>('common');
  const [leftOverflow, setLeftOverflow] = useState(false);
  const [rightOverflow, setRightOverflow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(undefined);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (items.length === 0) {
        return;
      }

      if (
        !event.altKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey
      ) {
        if (event.key === 'ArrowRight') {
          const activeItemIndex = activeItem
            ? items.findIndex((item) => item.id === activeItem.id)
            : -1;

          onActiveItemChange(items[(activeItemIndex + 1) % items.length]!);
        } else {
          // This eslint error could not be resolved so we tended to resolve it according to this URL:
          // https://github.com/gotwarlost/istanbul/issues/781
          // istanbul ignore else
          // eslint-disable-next-line no-lonely-if
          if (event.key === 'ArrowLeft') {
            const activeItemIndex = activeItem
              ? items.findIndex((item) => item.id === activeItem.id)
              : items.length;

            onActiveItemChange(
              items[(activeItemIndex + items.length - 1) % items.length]!
            );
          }
        }
      }
    },
    [activeItem, onActiveItemChange, items]
  );

  const checkOverflow = useCallback(() => {
    if (containerRef.current) {
      setLeftOverflow(containerRef.current.scrollLeft > 0);
      setRightOverflow(
        containerRef.current.scrollLeft <
          containerRef.current.scrollWidth - containerRef.current.offsetWidth
      );
    }
  }, []);

  useMountEffect(() => {
    window.addEventListener('resize', checkOverflow, true);

    return (): void => {
      window.removeEventListener('resize', checkOverflow, true);
    };
  });

  const latestAdjustmentId = useRef('');

  // this code block is responsible for keeping the active tab in the view
  //
  // testing this block of code provides no value as
  // jest does not provide infrastructure for testing scrolling items and we
  // need to manually simulate all properties
  /* istanbul ignore next */
  const activeRefCallback = useCallback(
    (element: HTMLAnchorElement | null) => {
      if (
        element &&
        ref.current &&
        (ref.current.contains(document.activeElement) ||
          ref.current === document.activeElement)
      ) {
        element.focus();
      }

      const thisAdjustmentId = uuid();

      latestAdjustmentId.current = thisAdjustmentId;

      function setScrollLeft(): void {
        if (latestAdjustmentId.current !== thisAdjustmentId) {
          return;
        }
        if (element && containerRef.current) {
          if (
            containerRef.current.scrollLeft <
            Math.min(
              element.offsetLeft +
                element.offsetWidth -
                containerRef.current.offsetWidth +
                48,
              containerRef.current.scrollWidth -
                containerRef.current.offsetWidth
            )
          ) {
            // if the chosen active tab is not completely in the view it will change the "scrollLeft" of the container
            // of the tabs so that the active tab comes into view with 48px from right.
            // (if there are still some tabs on the right)
            containerRef.current.scrollLeft =
              element.offsetLeft +
              element.offsetWidth -
              containerRef.current.offsetWidth +
              48;
            checkOverflow();
            requestAnimationFrame(setScrollLeft);
          }
          // if the chosen active tab is not completely in the view it will change the "scrollLeft" of the container
          // of the tabs so that the active tab comes into view with 48px from left.
          // (if there are still some tabs on the left)
          else if (
            containerRef.current.scrollLeft >
            Math.max(element.offsetLeft - 48, 0)
          ) {
            containerRef.current.scrollLeft = element.offsetLeft - 48;
            checkOverflow();
            requestAnimationFrame(setScrollLeft);
          }
        }
      }
      requestAnimationFrame(setScrollLeft);
    },
    [checkOverflow]
  );

  const containerRefCallback = useCallback(
    (element: HTMLDivElement | null) => {
      containerRef.current = element;

      checkOverflow();
    },
    [checkOverflow]
  );

  const handleScrollLeft = useCallback(() => {
    /* istanbul ignore else */
    if (containerRef.current) {
      containerRef.current.scrollLeft = Math.max(
        containerRef.current.scrollLeft - containerRef.current.offsetWidth / 2,
        0
      );
      checkOverflow();
    }
  }, [checkOverflow]);

  const handleScrollRight = useCallback(() => {
    /* istanbul ignore else */
    if (containerRef.current) {
      containerRef.current.scrollLeft = Math.min(
        containerRef.current.scrollLeft + containerRef.current.offsetWidth / 2,
        containerRef.current.scrollWidth - containerRef.current.offsetWidth
      );
      checkOverflow();
    }
  }, [checkOverflow]);

  const selectBoxItems = useMemo(
    () => [
      ...items,
      ...(onCloseAll
        ? [
            {
              callback: onCloseAll,
              id: 'close-all',
              section: 1,
              text: t('actions.closeAllTabs'),
            } as unknown as T,
          ]
        : []),
    ],
    [items, t, onCloseAll]
  );

  return (
    <div style={{display: 'grid'}}>
      <StyledTabBar
        ref={ref}
        className={className}
        data-testid='tabbar'
        role='tablist'
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        {leftOverflow ? (
          <StyledChevron>
            <EIconSmallButton
              testId='left-overflow-button'
              onClick={handleScrollLeft}
            >
              <ChevronLargeLeft />
            </EIconSmallButton>
          </StyledChevron>
        ) : null}
        <FadeElementsContainer>
          <TabsContainer
            ref={containerRefCallback}
            data-testid='tabs-container'
          >
            <ScrollWidthExtenderLeft />
            {items.map((item) => (
              <ItemComponent
                key={item.id}
                ref={
                  item.id === activeItem?.id ? activeRefCallback : checkOverflow
                }
                active={item.id === activeItem?.id}
                item={item}
                width={item.width}
                onActivate={onActiveItemChange}
                onClose={onClose}
              />
            ))}
            <ScrollWidthExtenderRight />
          </TabsContainer>
          {leftOverflow ? <LeftFade data-testid='left-fade' /> : null}
          {rightOverflow ? <RightFade data-testid='right-fade' /> : null}
        </FadeElementsContainer>
        {rightOverflow ? (
          <StyledChevron>
            <EIconSmallButton
              testId='right-overflow-button'
              onClick={handleScrollRight}
            >
              <ChevronLargeRight />
            </EIconSmallButton>
          </StyledChevron>
        ) : null}
        {leftOverflow || rightOverflow ? (
          <SelectBox
            Icon={ChevronDown}
            items={selectBoxItems}
            placement={Positioning.BottomRightAlignDown}
            startingPoint={SelectBoxStartingPoint.Icon}
            onItemChange={onActiveItemChange}
          />
        ) : null}
      </StyledTabBar>
    </div>
  );
}

export default typedObserver(TabBarComponent);

export type {Item};
